/**
 * ACTIVE.1 — Primary Stats.
 *
 * `weapon-primary-basic-attack-contract.md` §1-2. The six ordinary freely
 * allocated Primary Stats. Invested points count at face value: every
 * downstream Weapon×Primary formula reads the raw number. Deliberately separate
 * from `src/sim/stats.ts`'s `BaseStats`/`EffectiveStats` — those are outgoing
 * combat-modifier structures (globalDamage/attributeDamage/attackTypeDamage/
 * crit), not this six-stat allocation model (audit §D).
 */

import { isFiniteNumber } from '../math.js';

export type PrimaryStatKey = 'STR' | 'AGI' | 'TEC' | 'VIT' | 'MAG' | 'WIL';

/** Canonical stat order — never rely on object insertion order for anything
 * that must be deterministic. */
export const PRIMARY_STAT_KEYS: readonly PrimaryStatKey[] = ['STR', 'AGI', 'TEC', 'VIT', 'MAG', 'WIL'];

export interface PrimaryStats {
  STR: number;
  AGI: number;
  TEC: number;
  VIT: number;
  MAG: number;
  WIL: number;
}

/** The neutral identity element — not a runtime-generated default (work
 * instruction §158): a fixed, versioned constant, safe to substitute for an
 * owner who has not yet allocated any Primary points (zero-stat invariant,
 * contract §1.2/§28). */
export const ZERO_PRIMARY_STATS: Readonly<PrimaryStats> = Object.freeze({ STR: 0, AGI: 0, TEC: 0, VIT: 0, MAG: 0, WIL: 0 });

export interface PrimaryStatsValidationResult {
  ok: boolean;
  errors: string[];
}

/** finite, non-negative, no arbitrary extra stat names. */
export function validatePrimaryStats(value: unknown): PrimaryStatsValidationResult {
  const errors: string[] = [];
  if (typeof value !== 'object' || value === null) {
    return { ok: false, errors: ['PrimaryStats must be an object'] };
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!PRIMARY_STAT_KEYS.includes(key as PrimaryStatKey)) {
      errors.push(`unknown Primary stat key "${key}"`);
    }
  }
  for (const key of PRIMARY_STAT_KEYS) {
    const v = record[key];
    if (!isFiniteNumber(v) || v < 0) {
      errors.push(`PrimaryStats.${key} must be a finite value >= 0 (got ${String(v)})`);
    }
  }
  return { ok: errors.length === 0, errors };
}
